import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-apollo';
import { NavLink, Redirect, useLocation } from 'react-router-dom';
import withStyles from 'react-jss';
import { Button, Header, Form, Message, Card } from 'semantic-ui-react';

import { ClassProp } from 'helpers/types';
import {
  noBlankPasswords,
  sanitizeMessage,
} from 'components/UserMgmt/UserMgmt.helpers';
import Loading from 'components/Loading';
import styles from 'components/UserMgmt/UserMgmt.styles';
import EmailInput from 'components/FormComponents/EmailInput';
import PasswordInput from 'components/FormComponents/PasswordInput';
import { CREATE_SPLITIO_CLIENT } from 'components/UserMgmt/UserMgmt.queries';
import { useSplitIO } from 'components/Splitio/useSplitIO';

import MFAForm from '../MFA/MFAForm';
import LoginManager from 'helpers/loginManager';

export type Values = {
  email: string;
  password: string;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

const oktaLogin = () => {
  // OIDC Sign In
  window.location.assign(
    `https://${process.env.COGNITO_DOMAIN}/oauth2/authorize?identity_provider=${process.env.COGNITO_ID_PROVIDER}&redirect_uri=${process.env.COGNITO_REDIRECT_SIGN_IN}&response_type=${process.env.COGNITO_RESPONSE_TYPE}&client_id=${process.env.COGNITO_USER_POOL_WEB_CLIENT_ID}&scope=${process.env.COGNITO_SCOPES}`,
  );
};

type LoginFormData = {
  email: string;
  password: string;
  verificationCode: string;
};

const Login: FunctionComponent<Props> = ({ classes }) => {
  const location = useLocation();
  const hash = location.hash; // workaround as cognito will include '#' in temporary passwords breaking the password query param
  const searchParams = new URLSearchParams(location.search);
  const password = searchParams.get('tempPassword') ?? '';
  const email = searchParams.get('email') ?? '';
  const methods = useForm<LoginFormData>({
    mode: 'onBlur',
    defaultValues: {
      email,
      password: `${password}${hash}`,
    },
  });

  const { handleSubmit, watch, formState, register } = methods;
  const [createSplitioClient] = useMutation(CREATE_SPLITIO_CLIENT);
  const { status: useMFAFlow } = useSplitIO('auth_split');

  useEffect(() => {
    createSplitioClient({ variables: { key: 'x' } });
  }, []);

  useEffect(() => {
    if (useMFAFlow) {
      LoginManager.getInstance().setCustomAuthFlow();
    }
  }, [useMFAFlow]);

  const [isSubmitting, setSubmitting] = useState(false);
  const [needsVerificationCode, setNeedsVerificationCode] = useState(false);
  const [errors, setErrors] = useState<string>();
  const [requireNewPassword, setRequireNewPassword] = useState<boolean>(false);

  const watchFields = watch(['email', 'password']);

  const onSubmit = async ({ email, password }: LoginFormData) => {
    setSubmitting(true);

    try {
      const {
        needsVerificationCode,
        requireNewPassword,
      } = await LoginManager.getInstance().signIn(email, password);

      if (needsVerificationCode) {
        setNeedsVerificationCode(true);
      }
      if (requireNewPassword) {
        setRequireNewPassword(true);
      }
    } catch (error) {
      const errorMsg = sanitizeMessage(
        error as Record<string, string>,
        'Incorrect username or password.',
      );
      setErrors(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (requireNewPassword) {
    return (
      <Redirect
        to={{
          pathname: `/reset-password`,
          state: {
            email: watchFields.email,
            password: watchFields.password,
          },
        }}
      />
    );
  }

  return (
    <>
      {needsVerificationCode ? (
        <MFAForm />
      ) : (
        <>
          <Header className={classes.header} as="h1">
            Welcome Back!
          </Header>
          <Card className={classes.container} fluid centered>
            <Card.Content>
              {isSubmitting && <Loading loading />}
              <FormProvider {...methods}>
                <Form onSubmit={handleSubmit(onSubmit)} error={!!errors}>
                  {errors && <Message error content={errors} />}

                  <Form.Field className={classes.field}>
                    <EmailInput
                      label="Email"
                      name="email"
                      placeholder="Enter your Email"
                      autoComplete="username"
                    />
                  </Form.Field>

                  <Form.Field className={classes.field}>
                    <PasswordInput
                      label="Password"
                      name="password"
                      autoComplete="current-password"
                      placeholder="Enter your New Password"
                      register={register({
                        required: noBlankPasswords,
                      })}
                    />
                  </Form.Field>
                  <Button
                    as={NavLink}
                    to={'/forgot-password'}
                    floated={'right'}
                    className={classes.backNavLink}
                  >
                    Forgot password?
                  </Button>
                  <Form.Field>
                    <Button
                      fluid
                      type="submit"
                      className={classes.button}
                      disabled={!formState.isValid || isSubmitting}
                    >
                      Log in
                    </Button>
                  </Form.Field>
                </Form>
              </FormProvider>
            </Card.Content>
            <Card.Content>
              <Button
                fluid
                onClick={oktaLogin}
                className={`${classes.button} ${classes.buttonSecond}`}
              >
                Log in with Okta
              </Button>
            </Card.Content>
          </Card>
        </>
      )}
    </>
  );
};

export default withStyles(styles)(Login);
